/**
 * Settings Page
 * 
 * Application settings and user preferences.
 */

import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  User,
  Bell,
  Monitor,
  Shield,
} from 'lucide-react'
import clsx from 'clsx'

/**
 * Settings section component.
 */
function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ElementType
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="card">
      <div className="flex items-start gap-4 mb-4">
        <div className="p-3 bg-surface-overlay rounded-lg">
          <Icon className="text-text-secondary" size={20} />
        </div>
        <div>
          <h3 className="font-semibold text-text-primary">{title}</h3>
          <p className="text-sm text-text-secondary">{description}</p>
        </div>
      </div>
      <div className="border-t border-border pt-4">{children}</div>
    </div>
  )
}

/**
 * Toggle setting component.
 */
function ToggleSetting({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-text-primary">{label}</p>
        {description && <p className="text-sm text-text-muted">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
          checked ? 'bg-synth-mint' : 'bg-neutral-600'
        )}
      >
        <span
          className={clsx(
            'inline-block h-4 w-4 transform rounded-full bg-surface-app transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1'
          )}
        />
      </button>
    </div>
  )
}

/**
 * Settings page component.
 */
export default function Settings() {
  const { user } = useAuth()
  
  // Settings state (would normally be loaded from API)
  const [settings, setSettings] = useState({
    notifications: true,
    soundAlerts: false,
    darkMode: true,
    compactMode: false,
  })
  
  const updateSetting = (key: keyof typeof settings, value: boolean) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
    // TODO: Save to API
  }
  
  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Settings</h2>
        <p className="text-text-secondary">
          Manage your account and application preferences.
        </p>
      </div>
      
      {/* Account section */}
      <SettingsSection
        icon={User}
        title="Account"
        description="Your Twitch account information"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-synth-magenta flex items-center justify-center">
              <span className="text-2xl font-bold text-text-on-accent">
                {user?.username?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <p className="text-lg font-medium text-text-primary">{user?.username}</p>
              <p className="text-sm text-text-secondary">Twitch ID: {user?.twitchId}</p>
              {user?.isAdmin && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-synth-magenta/20 text-synth-magenta text-xs rounded">
                  Admin
                </span>
              )}
            </div>
          </div>
        </div>
      </SettingsSection>
      
      {/* Notifications section */}
      <SettingsSection
        icon={Bell}
        title="Notifications"
        description="Configure how you receive updates"
      >
        <div className="space-y-2">
          <ToggleSetting
            label="Browser Notifications"
            description="Receive notifications for important events"
            checked={settings.notifications}
            onChange={(v) => updateSetting('notifications', v)}
          />
          <ToggleSetting
            label="Sound Alerts"
            description="Play sounds for new events"
            checked={settings.soundAlerts}
            onChange={(v) => updateSetting('soundAlerts', v)}
          />
        </div>
      </SettingsSection>
      
      {/* Display section */}
      <SettingsSection
        icon={Monitor}
        title="Display"
        description="Customize the appearance"
      >
        <div className="space-y-2">
          <ToggleSetting
            label="Dark Mode"
            description="Use dark color scheme"
            checked={settings.darkMode}
            onChange={(v) => updateSetting('darkMode', v)}
          />
          <ToggleSetting
            label="Compact Mode"
            description="Reduce spacing for more content"
            checked={settings.compactMode}
            onChange={(v) => updateSetting('compactMode', v)}
          />
        </div>
      </SettingsSection>
      
      {/* Security section */}
      <SettingsSection
        icon={Shield}
        title="Security"
        description="Manage your session and access"
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-text-secondary mb-2">Session</p>
            <p className="text-text-primary">
              You are signed in via Twitch OAuth.
            </p>
          </div>
          
          <div className="pt-2">
            <button className="btn-danger">
              Sign Out of All Devices
            </button>
          </div>
        </div>
      </SettingsSection>
      
      {/* About section */}
      <div className="card bg-surface-raised/50">
        <div className="text-center text-sm text-text-muted">
          <p>PratBot v0.1.0</p>
          <p className="mt-1">
            Made with ❤️ for streamers
          </p>
        </div>
      </div>
    </div>
  )
}
